import { ThemeProvider, createTheme } from '@mui/material'
import OfflineDialog from 'components/BottomSheets/OfflineFullScreen'
import { m3Theme } from 'config/theme/m3Theme'
import i18next from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { I18nextProvider, initReactI18next } from 'react-i18next'
import 'react-toastify/dist/ReactToastify.css'
import Logger from 'utils/Logger'
import { isMalaysia } from 'utils/tenant'
import * as Colors from './appconstants/AppColors'
import ns1 from './i18n/en/ns1.json'
import Pages from './routes/Pages'
import { useInactivityTimeout } from 'hooks/useInactivityTimeout'

i18next.use(initReactI18next).init({
  lng: 'en',
  debug: process.env.NODE_ENV === 'development',
  resources: { en: { ns1 } },
  defaultNS: 'ns1',
  missingKeyHandler: (lng, ns, key, fallbackValue) => {
    Logger.logWarning({
      message: 'i18n Missing Key',
      options: { lng, ns, key, fallbackValue },
    })
  },
})

const theme = createTheme({
  typography: {
    fontFamily: 'Indivisble',
    fontWeightMedium: 500,
    fontWeightRegular: 400,
    fontWeightLight: 200,
    fontWeightBold: 600,
    h1: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: '20px',
    },
    h2: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: '20px',
    },
    h3: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '20px',
    },
  },
  palette: {
    primary: {
      main: Colors.AppGreen,
      light: Colors.AppGrey,
    },
    secondary: {
      main: Colors.AppBlack,
      light: Colors.AppLightBlue,
    },
    grey: {
      50: Colors.AppBorderGrey,
    },
    text: {
      primary: Colors.AppBlack,
      disabled: Colors.AppTextDisabled,
      secondary: Colors.AppWhite,
    },
    action: {
      active: Colors.AppGreen,
      focus: Colors.AppGreen,
      disabledBackground: Colors.AppGrey,
    },
  },
})

function App() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [activeTheme, setActiveTheme] = useState(isMalaysia() ? m3Theme : theme)

  // Initialize inactivity timeout tracking
  useInactivityTimeout()

  useEffect(() => {
    // Check if theme needs to be updated whenever component renders
    const currentTheme = isMalaysia() ? m3Theme : theme
    setActiveTheme(currentTheme)
  }, [])

  useEffect(() => {
    function onlineHandler() {
      setIsOffline(false)
    }

    function offlineHandler() {
      setIsOffline(true)
    }

    const checkThemeInterval = setInterval(() => {
      const shouldUseMysTheme = isMalaysia()
      const newTheme = shouldUseMysTheme ? m3Theme : theme
      setActiveTheme(newTheme)
      document.body.classList.toggle('mys-region', shouldUseMysTheme)
    }, 10)

    window.addEventListener('online', onlineHandler)
    window.addEventListener('offline', offlineHandler)

    Logger.initializeLogger({
      url: process.env.REACT_APP_LOGGER_URL,
      client: process.env.REACT_APP_LOGGER_CLIENT_ID,
      debounce: +process.env.REACT_APP_LOGGER_DEBOUNCE_TIME_IN_MS || 10000,
      maxStackSize: +process.env.REACT_APP_LOGGER_MAX_QUEUE_SIZE || 5,
    })

    document.body.classList.toggle('mys-region', isMalaysia())

    return () => {
      window.removeEventListener('online', onlineHandler)
      window.removeEventListener('offline', offlineHandler)
      clearInterval(checkThemeInterval)
      document.body.classList.remove('mys-region')
    }
  }, [])

  return (
    <ThemeProvider theme={activeTheme}>
      <HelmetProvider>
        <I18nextProvider i18n={i18next}>
          <Pages />
        </I18nextProvider>
      </HelmetProvider>
      <OfflineDialog openDialog={isOffline} />
    </ThemeProvider>
  )
}

export default App
